import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
    Activity, Archive, ArrowRight, CalendarDays, Car, ChevronRight, Crown,
    Flag, History as HistoryIcon, Newspaper, RefreshCw, Trophy, Users, type LucideIcon,
} from 'lucide-react';
import { apiRequest } from '../utils/api';
import { PageHero } from '../components/PageHero';
import { Button } from '../components/UI';
import { API_BASE_URL } from '../config';
import { NewsItem, Season, SeasonStatus } from '../types';

interface ArchiveEvent {
    id: string;
    seasonId: string;
    roundNumber: number;
    date: string;
    status: string;
    isSprint: boolean | number;
    isTestRace?: boolean | number;
    trackName: string;
    trackLocation: string;
    trackFlag?: string;
}

interface DriverStanding {
    id: string;
    username: string;
    teamName?: string;
    teamColor?: string;
    totalPoints: number;
    wins: number;
    podiums: number;
    races: number;
}

interface ConstructorStanding {
    id: string;
    name: string;
    teamColor?: string;
    teamLogo?: string;
    carImageUrl?: string;
    totalPoints: number;
    wins: number;
}

interface StandingsData {
    drivers: DriverStanding[];
    constructors: ConstructorStanding[];
    seasonName?: string;
}

type ArchiveTab = 'OVERVIEW' | 'RACES' | 'STANDINGS' | 'NEWS';

const getAssetUrl = (url?: string) => {
    if (!url) return undefined;
    if (url.startsWith('http') || url.startsWith('data:')) return url;
    const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
    if (!baseUrl) return url;
    return url.startsWith('/') ? `${baseUrl}${url}` : `${baseUrl}/${url}`;
};

const plainText = (html: string) => html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

export const History: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [seasons, setSeasons] = useState<Season[]>([]);
    const [selectedSeasonId, setSelectedSeasonId] = useState(searchParams.get('seasonId') || '');
    const [events, setEvents] = useState<ArchiveEvent[]>([]);
    const [standings, setStandings] = useState<StandingsData>({ drivers: [], constructors: [] });
    const [news, setNews] = useState<NewsItem[]>([]);
    const [tab, setTab] = useState<ArchiveTab>('OVERVIEW');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        apiRequest<Season[]>('seasons.php?all=true')
            .then(items => {
                const finished = (Array.isArray(items) ? items : [])
                    .filter(item => item.status === SeasonStatus.FINISHED)
                    .sort((a, b) => Number(b.year) - Number(a.year));
                setSeasons(finished);
                const requested = searchParams.get('seasonId');
                const initial = finished.some(item => item.id === requested) ? requested! : finished[0]?.id || '';
                setSelectedSeasonId(initial);
                if (initial) setSearchParams({ seasonId: initial }, { replace: true });
                else setLoading(false);
            })
            .catch(() => {
                setError('Die Historie konnte nicht geladen werden.');
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (!selectedSeasonId) return;
        setLoading(true);
        setError('');
        Promise.all([
            apiRequest<ArchiveEvent[]>(`events.php?seasonId=${encodeURIComponent(selectedSeasonId)}`),
            apiRequest<StandingsData>(`standings.php?seasonId=${encodeURIComponent(selectedSeasonId)}`),
            apiRequest<NewsItem[]>(`news.php?seasonId=${encodeURIComponent(selectedSeasonId)}&limit=200`),
        ])
            .then(([eventData, standingsData, newsData]) => {
                setEvents((Array.isArray(eventData) ? eventData : []).sort((a, b) => Number(a.roundNumber) - Number(b.roundNumber)));
                setStandings(standingsData || { drivers: [], constructors: [] });
                setNews(Array.isArray(newsData) ? newsData : []);
            })
            .catch(() => setError('Die Daten dieser Saison konnten nicht vollständig geladen werden.'))
            .finally(() => setLoading(false));
    }, [selectedSeasonId]);

    const selectedSeason = seasons.find(item => item.id === selectedSeasonId);
    const champion = standings.drivers[0];
    const constructorChampion = standings.constructors[0];
    const completedEvents = events.filter(item => item.status === 'COMPLETED');
    const newsByEvent = useMemo(() => news.reduce<Record<string, NewsItem[]>>((groups, item) => {
        if (item.event_id) (groups[item.event_id] ||= []).push(item);
        return groups;
    }, {}), [news]);

    const changeSeason = (id: string) => {
        setSelectedSeasonId(id);
        setSearchParams({ seasonId: id }, { replace: true });
        setTab('OVERVIEW');
    };

    const tabs: { id: ArchiveTab; label: string; icon: LucideIcon }[] = [
        { id: 'OVERVIEW', label: 'Überblick', icon: Archive },
        { id: 'RACES', label: 'Rennen', icon: Flag },
        { id: 'STANDINGS', label: 'Tabellen & Teams', icon: Trophy },
        { id: 'NEWS', label: 'News', icon: Newspaper },
    ];

    return (
        <div className="mx-auto max-w-7xl space-y-6 pb-24 animate-fade-in">
            <PageHero
                eyebrow="SDC Archiv"
                title="Historie"
                description="Abgeschlossene Saisons an einem Ort: Rennkalender, Ergebnisse, Meisterschaften, Teams, News und Race Analytics."
                icon={HistoryIcon}
                accent="#f59e0b"
                stats={[
                    { label: 'Saisons', value: seasons.length },
                    { label: 'Rennen', value: completedEvents.length },
                    { label: 'Meldungen', value: news.length },
                ]}
                actions={seasons.length > 0 ? (
                    <label className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/25 px-4 py-3">
                        <span className="text-[9px] font-black uppercase tracking-[0.18em] text-white/35">Saison</span>
                        <select value={selectedSeasonId} onChange={event => changeSeason(event.target.value)} className="bg-transparent text-xs font-black text-white outline-none">
                            {seasons.map(season => <option key={season.id} value={season.id}>{season.name} ({season.year})</option>)}
                        </select>
                    </label>
                ) : undefined}
            />

            {seasons.length === 0 && !loading ? (
                <div className="rounded-[2rem] border border-dashed border-white/10 bg-white/[0.025] p-12 text-center">
                    <Archive className="mx-auto mb-4 text-white/20" size={42} />
                    <h2 className="text-xl font-black uppercase italic text-white">Noch keine abgeschlossene Saison</h2>
                    <p className="mt-2 text-sm text-white/35">Sobald eine Saison beendet wurde, wird sie hier automatisch einsortiert.</p>
                </div>
            ) : (
                <>
                    <div className="flex gap-2 overflow-x-auto rounded-2xl border border-white/[0.07] bg-[#101117] p-2">
                        {tabs.map(item => {
                            const Icon = item.icon;
                            return (
                                <button key={item.id} onClick={() => setTab(item.id)} className={`flex shrink-0 items-center gap-2 rounded-xl px-4 py-2.5 text-[10px] font-black uppercase tracking-[0.12em] transition ${tab === item.id ? 'bg-white text-black' : 'text-white/40 hover:bg-white/[0.05] hover:text-white'}`}>
                                    <Icon size={14} />{item.label}
                                </button>
                            );
                        })}
                    </div>

                    {loading ? (
                        <div className="py-28 text-center"><RefreshCw className="mx-auto animate-spin text-white/25" /></div>
                    ) : error ? (
                        <div className="rounded-2xl border border-red-500/20 bg-red-500/10 p-6 text-sm text-red-200">{error}</div>
                    ) : (
                        <>
                            {tab === 'OVERVIEW' && (
                                <div className="space-y-6">
                                    <section className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                                        {[
                                            { label: 'Saison', value: selectedSeason?.name || standings.seasonName || '–', sub: String(selectedSeason?.year || ''), icon: CalendarDays, color: '#f59e0b' },
                                            { label: 'Fahrer-Weltmeister', value: champion?.username || 'Noch offen', sub: champion ? `${Number(champion.totalPoints).toFixed(0)} Punkte` : '', icon: Crown, color: '#facc15' },
                                            { label: 'Konstrukteurs-WM', value: constructorChampion?.name || 'Noch offen', sub: constructorChampion ? `${Number(constructorChampion.totalPoints).toFixed(0)} Punkte` : '', icon: Trophy, color: constructorChampion?.teamColor || '#ef4444' },
                                            { label: 'Rennwochenenden', value: String(completedEvents.length), sub: `${news.length} zugeordnete News`, icon: Flag, color: '#3b82f6' },
                                        ].map(card => (
                                            <article key={card.label} className="relative overflow-hidden rounded-[1.7rem] border border-white/[0.07] bg-[#12131a] p-5">
                                                <div className="absolute inset-x-0 top-0 h-[2px]" style={{ background: `linear-gradient(90deg, ${card.color}, transparent)` }} />
                                                <card.icon size={18} style={{ color: card.color }} />
                                                <p className="mt-5 text-[9px] font-black uppercase tracking-[0.17em] text-white/30">{card.label}</p>
                                                <p className="mt-1 truncate text-xl font-black uppercase italic text-white">{card.value}</p>
                                                <p className="mt-1 text-xs text-white/35">{card.sub}</p>
                                            </article>
                                        ))}
                                    </section>
                                    <section className="grid gap-4 lg:grid-cols-[1.35fr_.65fr]">
                                        <div className="rounded-[2rem] border border-white/[0.07] bg-[#111218] p-5 md:p-6">
                                            <div className="mb-5 flex items-center justify-between">
                                                <div><p className="text-[9px] font-black uppercase tracking-[0.2em] text-white/30">Saisonverlauf</p><h2 className="text-xl font-black uppercase italic text-white">Rennwochenenden</h2></div>
                                                <button onClick={() => setTab('RACES')} className="text-[9px] font-black uppercase tracking-wider text-amber-300">Alle ansehen</button>
                                            </div>
                                            <div className="space-y-2">
                                                {completedEvents.slice(-5).reverse().map(event => (
                                                    <Link key={event.id} to={`/event/${event.id}`} className="flex items-center gap-3 rounded-2xl border border-white/[0.05] bg-black/20 p-3 transition hover:border-white/15">
                                                        <span className="w-9 text-center font-mono text-xs font-black text-white/25">R{event.roundNumber}</span>
                                                        <div className="min-w-0 flex-1"><p className="truncate text-sm font-black uppercase italic text-white">{event.trackLocation}</p><p className="text-[10px] text-white/30">{new Date(event.date).toLocaleDateString('de-DE')}</p></div>
                                                        <ChevronRight size={16} className="text-white/20" />
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="rounded-[2rem] border border-white/[0.07] bg-[#111218] p-5 md:p-6">
                                            <p className="text-[9px] font-black uppercase tracking-[0.2em] text-white/30">Top 3</p>
                                            <h2 className="mb-5 text-xl font-black uppercase italic text-white">Fahrer-WM</h2>
                                            <div className="space-y-3">
                                                {standings.drivers.slice(0, 3).map((driver, index) => (
                                                    <Link key={driver.id} to={`/driver/${driver.id}`} className="flex items-center gap-3 rounded-2xl border border-white/[0.05] bg-black/20 p-3">
                                                        <span className="font-mono text-lg font-black text-white/25">{index + 1}</span>
                                                        <span className="h-8 w-1 rounded-full" style={{ backgroundColor: driver.teamColor || '#555' }} />
                                                        <div className="min-w-0 flex-1"><p className="truncate text-sm font-black text-white">{driver.username}</p><p className="truncate text-[9px] text-white/30">{driver.teamName || 'Ohne Team'}</p></div>
                                                        <span className="font-mono text-xs font-black text-white">{Number(driver.totalPoints).toFixed(0)}</span>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    </section>
                                </div>
                            )}

                            {tab === 'RACES' && (
                                <div className="grid gap-4 lg:grid-cols-2">
                                    {events.map(event => {
                                        const relatedNews = newsByEvent[event.id] || [];
                                        return (
                                            <article key={event.id} className="group relative overflow-hidden rounded-[2rem] border border-white/[0.07] bg-[#111218] p-5 md:p-6">
                                                {event.trackFlag && <img src={getAssetUrl(event.trackFlag)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-[0.06] transition group-hover:opacity-[0.1]" />}
                                                <div className="relative">
                                                    <div className="flex items-start justify-between gap-4">
                                                        <div className="flex gap-3"><span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-black/30 font-mono text-xs font-black text-white/40">R{event.roundNumber}</span><div><p className="text-[9px] font-black uppercase tracking-[0.16em] text-white/30">{new Date(event.date).toLocaleDateString('de-DE')}</p><h2 className="text-2xl font-black uppercase italic text-white">{event.trackLocation}</h2><p className="text-xs text-white/35">{event.trackName}{Number(event.isSprint) === 1 ? ' · Sprint' : ''}</p></div></div>
                                                        <span className="rounded-full border border-emerald-400/15 bg-emerald-400/10 px-3 py-1 text-[8px] font-black uppercase tracking-wider text-emerald-300">Beendet</span>
                                                    </div>
                                                    <div className="mt-5 flex flex-wrap gap-2">
                                                        <Link to={`/event/${event.id}`} className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-[9px] font-black uppercase tracking-wider text-black"><Trophy size={13} /> Ergebnisse</Link>
                                                        <Link to={`/race-analytics?eventId=${event.id}`} className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.05] px-4 py-2.5 text-[9px] font-black uppercase tracking-wider text-white"><Activity size={13} /> Race Analytics</Link>
                                                    </div>
                                                    {relatedNews.length > 0 && <div className="mt-5 border-t border-white/[0.06] pt-4"><p className="mb-2 text-[8px] font-black uppercase tracking-[0.18em] text-white/25">Zugeordnete Meldungen</p>{relatedNews.slice(0, 3).map(item => <button key={item.id} onClick={() => setTab('NEWS')} className="mr-2 mt-1 inline-flex items-center gap-1.5 rounded-full border border-blue-400/15 bg-blue-400/[0.07] px-3 py-1.5 text-[9px] font-bold text-blue-200"><Newspaper size={11} />{item.title}</button>)}</div>}
                                                </div>
                                            </article>
                                        );
                                    })}
                                </div>
                            )}

                            {tab === 'STANDINGS' && (
                                <div className="grid gap-5 lg:grid-cols-2">
                                    <section className="overflow-hidden rounded-[2rem] border border-white/[0.07] bg-[#111218]">
                                        <div className="border-b border-white/[0.06] p-5"><p className="text-[9px] font-black uppercase tracking-[0.18em] text-white/30">Endstand</p><h2 className="text-xl font-black uppercase italic text-white">Fahrer-WM</h2></div>
                                        <div className="divide-y divide-white/[0.045]">{standings.drivers.map((driver, index) => <Link key={driver.id} to={`/driver/${driver.id}`} className="grid grid-cols-[2rem_1fr_auto] items-center gap-3 px-5 py-3.5 transition hover:bg-white/[0.025]"><span className="font-mono text-xs font-black text-white/25">{index + 1}</span><div className="min-w-0"><p className="truncate text-sm font-black text-white">{driver.username}</p><p className="truncate text-[9px] text-white/30">{driver.teamName || 'Ohne Team'} · {driver.wins || 0} Siege</p></div><span className="font-mono text-sm font-black text-white">{Number(driver.totalPoints).toFixed(0)} <small className="text-[8px] text-white/25">PTS</small></span></Link>)}</div>
                                    </section>
                                    <section className="overflow-hidden rounded-[2rem] border border-white/[0.07] bg-[#111218]">
                                        <div className="border-b border-white/[0.06] p-5"><p className="text-[9px] font-black uppercase tracking-[0.18em] text-white/30">Teams der Saison</p><h2 className="text-xl font-black uppercase italic text-white">Konstrukteurs-WM</h2></div>
                                        <div className="divide-y divide-white/[0.045]">{standings.constructors.map((team, index) => <Link key={team.id} to={`/team/${team.id}`} className="grid grid-cols-[2rem_2.4rem_1fr_auto] items-center gap-3 px-5 py-3.5 transition hover:bg-white/[0.025]"><span className="font-mono text-xs font-black text-white/25">{index + 1}</span><span className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/[0.06] bg-black/20">{team.teamLogo ? <img src={getAssetUrl(team.teamLogo)} className="max-h-7 max-w-7 object-contain" alt="" /> : <Car size={15} className="text-white/20" />}</span><div className="min-w-0"><p className="truncate text-sm font-black text-white">{team.name}</p><p className="text-[9px] text-white/30">{team.wins || 0} Siege</p></div><span className="font-mono text-sm font-black text-white">{Number(team.totalPoints).toFixed(0)} <small className="text-[8px] text-white/25">PTS</small></span></Link>)}</div>
                                    </section>
                                </div>
                            )}

                            {tab === 'NEWS' && (
                                <div className="grid gap-4 md:grid-cols-2">
                                    {news.length === 0 ? <div className="col-span-full rounded-[2rem] border border-dashed border-white/10 p-12 text-center text-sm text-white/30">Dieser Saison sind keine Meldungen zugeordnet.</div> : news.map(item => (
                                        <article key={item.id} className="rounded-[2rem] border border-white/[0.07] bg-[#111218] p-5 md:p-6">
                                            <div className="flex items-center justify-between gap-3"><span className="rounded-full border border-blue-400/15 bg-blue-400/[0.07] px-3 py-1 text-[8px] font-black uppercase tracking-wider text-blue-200">{item.category}</span><span className="text-[9px] text-white/25">{new Date(item.published_at || item.created_at).toLocaleDateString('de-DE')}</span></div>
                                            <h2 className="mt-4 text-xl font-black uppercase italic leading-tight text-white">{item.title}</h2>
                                            <p className="mt-3 line-clamp-3 text-sm leading-6 text-white/40">{plainText(item.content)}</p>
                                            <div className="mt-5 flex items-center justify-between border-t border-white/[0.06] pt-4"><span className="text-[9px] font-bold text-white/25">{item.track_location ? `Rennen: ${item.track_location}` : 'Saisonmeldung'}</span>{item.event_id && <Link to={`/event/${item.event_id}`} className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider text-amber-300">Zum Rennen <ArrowRight size={12} /></Link>}</div>
                                        </article>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
};
